import { FormEvent, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { BezpartHeader } from "../components/BezpartHeader";
import { CabinetBoardRow } from "../components/cabinet/CabinetBoardRow";
import { CabinetEmptyState } from "../components/cabinet/CabinetEmptyState";
import { CabinetLimitBanner } from "../components/cabinet/CabinetLimitBanner";
import { CabinetShareDialog } from "../components/cabinet/CabinetShareDialog";
import { formatCabinetUserName } from "../lib/mainSiteLinks";
import { groupBoardsByRecency, formatBytes } from "../lib/cabinetRecentGroups";
import { isFetchAborted, isUnauthorizedError, authEntryPathForError } from "../lib/authRedirect";
import "../styles/air-pages.css";
import {
  apiFetch,
  type BoardRecording,
  type BoardSummary,
  type CabinetNotification,
  type FolderRow,
  type FoldersResponse,
  type MeResponse,
  type SharedFolderRow,
  addBoardByLink,
  removeBoardShortcut,
  setBoardPinned,
} from "../api/client";

function IconClock() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconPinNav() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M9 3h6l-1 6 4 4v2H6v-2l4-4-1-6zM12 15v6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconPlusNav() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 5v14M5 12h14"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconStarNav() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 22 12 18.77 5.82 22 7 14.14l-5-4.87 6.91-1.01L12 2z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconMic() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3zM19 11a7 7 0 01-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconSearch() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
      <path d="M20 20l-4-4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function IconList() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 6h16M4 12h16M4 18h10"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconFolder() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 8a2 2 0 012-2h4l2 2h10a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconChevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
      className={open ? "wb-cabinet-chevron is-open" : "wb-cabinet-chevron"}
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type FolderTreeRow = { folder: FolderRow; depth: number; hasChildren: boolean };

function buildFolderTreeOptions(folders: FolderRow[], excludeIds?: Set<string>) {
  const folderIds = new Set(folders.map((f) => f.id));
  const byParent: Record<string, FolderRow[]> = {};
  folders.forEach((f) => {
    if (excludeIds?.has(f.id)) return;
    const parentInSet = f.parent_id && folderIds.has(f.parent_id) ? f.parent_id : "root";
    if (!byParent[parentInSet]) byParent[parentInSet] = [];
    byParent[parentInSet].push(f);
  });
  Object.values(byParent).forEach((arr) =>
    arr.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru"))
  );

  const rows: { id: string; label: string }[] = [];
  function walk(parentKey: string, depth: number) {
    (byParent[parentKey] || []).forEach((f) => {
      const prefix = depth > 0 ? `${"— ".repeat(depth)}` : "";
      rows.push({ id: f.id, label: `${prefix}${f.name}` });
      walk(f.id, depth + 1);
    });
  }
  walk("root", 0);
  return rows;
}

function formatBoardUpdated(iso: string): string {
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}.${mm}.${yyyy} · ${hh}:${min}`;
  } catch {
    return "";
  }
}

export function CabinetPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const folderParam = searchParams.get("folder");
  const sectionParam = searchParams.get("section");
  const viewParam = searchParams.get("view");
  const searchQuery = (searchParams.get("q") ?? "").trim();
  const cabinetSection = sectionParam === "shared" ? "shared" : "mine";
  const rawCabinetView =
    viewParam === "recent" || viewParam === "starred" || viewParam === "recordings"
      ? viewParam
      : "folder";
  const selectedFolderId =
    !folderParam || folderParam === "root" ? null : folderParam;

  const [userName, setUserName] = useState("");
  const [accountKind, setAccountKind] = useState<"school" | "external">("external");
  const showRecordingsNav = accountKind === "school";
  const cabinetView =
    rawCabinetView === "recordings" && !showRecordingsNav ? "folder" : rawCabinetView;
  const [planCode, setPlanCode] = useState<string | null>(null);
  const [maxBoardsLimit, setMaxBoardsLimit] = useState<number>(-1);
  const [canCreateBoardByPlan, setCanCreateBoardByPlan] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [sharedFolders, setSharedFolders] = useState<SharedFolderRow[]>([]);
  const [allBoards, setAllBoards] = useState<BoardSummary[]>([]);
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [newBoardTitle, setNewBoardTitle] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [searchDraft, setSearchDraft] = useState(searchQuery);
  const [duplicateBusyId, setDuplicateBusyId] = useState<string | null>(null);
  const [starBusyId, setStarBusyId] = useState<string | null>(null);
  const [addLinkOpen, setAddLinkOpen] = useState(false);
  const [addLinkValue, setAddLinkValue] = useState("");
  const [addLinkBusy, setAddLinkBusy] = useState(false);
  const [shareBoard, setShareBoard] = useState<BoardSummary | null>(null);
  const [recordings, setRecordings] = useState<BoardRecording[]>([]);
  const [recordingsLoading, setRecordingsLoading] = useState(false);
  const [notifications, setNotifications] = useState<CabinetNotification[]>([]);
  const [planBusy, setPlanBusy] = useState(false);
  const [adminBillingAllowed, setAdminBillingAllowed] = useState(false);
  const [adminSchoolAllowed, setAdminSchoolAllowed] = useState(false);
  const [adminExternalAllowed, setAdminExternalAllowed] = useState(false);
  const newFolderInputRef = useRef<HTMLInputElement | null>(null);

  const [renameBoard, setRenameBoard] = useState<{
    id: string;
    title: string;
    description: string;
  } | null>(
    null
  );
  const [moveBoardId, setMoveBoardId] = useState<string | null>(null);
  const [moveFolderId, setMoveFolderId] = useState("");
  const [deleteBoardId, setDeleteBoardId] = useState<string | null>(null);
  const [renameFolderOpen, setRenameFolderOpen] = useState(false);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [deleteFolderOpen, setDeleteFolderOpen] = useState(false);
  const [moveFolderTarget, setMoveFolderTarget] = useState<{
    id: string;
    parent_id: string | null;
  } | null>(null);
  const [moveFolderParentId, setMoveFolderParentId] = useState("");

  // До отрисовки: иначе кнопки на миг мелькали прежним цветом.
  useLayoutEffect(() => {
    // wb-page--air — воздушный стиль, как у главной (styles/air-pages.css).
    document.body.classList.add("wb-page--cabinet", "wb-page--cabinet-miro", "wb-page--air");
    return () => {
      document.body.classList.remove("wb-page--cabinet", "wb-page--cabinet-miro", "wb-page--air");
    };
  }, []);

  const folderById = useMemo(() => {
    const m: Record<string, FolderRow> = {};
    folders.forEach((f) => {
      m[f.id] = f;
    });
    sharedFolders.forEach((f) => {
      m[f.id] = f;
    });
    return m;
  }, [folders, sharedFolders]);

  const activeFolderList = cabinetSection === "shared" ? sharedFolders : folders;

  const selectedSharedFolder =
    cabinetSection === "shared" && selectedFolderId
      ? sharedFolders.find((f) => f.id === selectedFolderId)
      : undefined;

  const panelTitle =
    cabinetView === "recent"
      ? "Недавние"
      : cabinetView === "starred"
        ? "Избранное"
        : cabinetView === "recordings"
          ? "Записи звонков"
          : selectedFolderId === null
            ? "Без папки"
            : folderById[selectedFolderId]?.name || "Папка";

  const panelSub =
    cabinetView === "recent"
      ? "Доски, которые вы недавно открывали или редактировали."
      : cabinetView === "starred"
        ? "Доски, отмеченные звёздочкой."
        : cabinetView === "recordings"
          ? "Аудиозаписи школьных уроков. Обычные созвоны сюда не сохраняются."
          : cabinetSection === "shared"
      ? selectedFolderId === null
        ? "Папки и доски, к которым вас пригласили."
        : selectedSharedFolder
          ? `Владелец: ${selectedSharedFolder.owner_email} · ${selectedSharedFolder.membership_role === "viewer" ? "просмотр" : "редактор"}`
          : "Доски в выбранной папке."
      : selectedFolderId === null
        ? "Доски вне папок. Создайте папку слева, чтобы навести порядок."
        : "Доски в выбранной папке. Откройте доску, чтобы рисовать.";

  const setSection = useCallback(
    (section: "mine" | "shared") => {
      const next = new URLSearchParams(searchParams);
      if (section === "shared") next.set("section", "shared");
      else next.delete("section");
      next.delete("view");
      next.set("folder", "root");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setView = useCallback(
    (view: "recent" | "starred" | "recordings" | "folder") => {
      const next = new URLSearchParams(searchParams);
      if (view === "folder") next.delete("view");
      else next.set("view", view);
      if (view !== "folder") next.delete("folder");
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setFolder = useCallback(
    (fid: string | null) => {
      const next = new URLSearchParams(searchParams);
      next.delete("view");
      if (fid === null) next.set("folder", "root");
      else next.set("folder", fid);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const loadFolders = useCallback(async () => {
    const data = await apiFetch<FoldersResponse>("/api/v1/folders");
    setFolders(data.owned || data.folders || []);
    setSharedFolders(data.shared || []);
  }, []);

  const loadAllBoards = useCallback(async (query?: string) => {
    const q = (query ?? "").trim();
    const suffix = q ? `?q=${encodeURIComponent(q)}` : "";
    const data = await apiFetch<{ boards: BoardSummary[] }>(`/api/v1/boards${suffix}`);
    const boards = data.boards || [];
    setAllBoards(boards);
    return boards;
  }, []);

  const loadRecordings = useCallback(async () => {
    setRecordingsLoading(true);
    try {
      const data = await apiFetch<{ recordings: BoardRecording[] }>("/api/v1/recordings");
      setRecordings(data.recordings || []);
    } finally {
      setRecordingsLoading(false);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const data = await apiFetch<{ notifications: CabinetNotification[] }>(
        "/api/v1/notifications?limit=20"
      );
      setNotifications(data.notifications || []);
    } catch {
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const me = await apiFetch<MeResponse>("/api/v1/me", { signal: ac.signal });
        if (cancelled || isFetchAborted(ac.signal)) return;
        setUserName(formatCabinetUserName(me.email || ""));
        setAccountKind(me.account_kind === "school" ? "school" : "external");
        setPlanCode(me.plan?.code ?? null);
        if (me.plan_limits) {
          setMaxBoardsLimit(me.plan_limits.max_boards);
          setCanCreateBoardByPlan(Boolean(me.plan_limits.can_create_board));
        } else {
          setMaxBoardsLimit((me.plan?.code ?? "free") === "pro" ? 30 : (me.plan?.code ?? "free") === "business" || me.account_kind === "school" ? -1 : 3);
          setCanCreateBoardByPlan(true);
        }
        try {
          const adminMe = await apiFetch<{
            allowed: boolean;
            scopes?: { school: boolean; external: boolean };
          }>("/api/v1/admin/me", {
            signal: ac.signal,
          });
          if (!cancelled && !isFetchAborted(ac.signal)) {
            const scopes = adminMe.scopes ?? {
              school: adminMe.allowed,
              external: adminMe.allowed,
            };
            setAdminSchoolAllowed(Boolean(scopes.school));
            setAdminExternalAllowed(Boolean(scopes.external));
            setAdminBillingAllowed(Boolean(scopes.school || scopes.external));
          }
        } catch {
          if (!cancelled && !isFetchAborted(ac.signal)) {
            setAdminBillingAllowed(false);
            setAdminSchoolAllowed(false);
            setAdminExternalAllowed(false);
          }
        }
        setSessionReady(true);
        try {
          await loadFolders();
          await loadAllBoards(searchQuery);
          await loadNotifications();
        } catch (dataErr) {
          if (cancelled || isFetchAborted(ac.signal)) return;
          setErr(dataErr instanceof Error ? dataErr.message : "Не удалось загрузить данные");
        }
      } catch (e) {
        if (cancelled || isFetchAborted(ac.signal)) return;
        if (isUnauthorizedError(e)) {
          navigate(authEntryPathForError(e, "/cabinet"), { replace: true });
          return;
        }
        setErr(e instanceof Error ? e.message : "Ошибка загрузки");
        setSessionReady(true);
      }
    })();

    return () => {
      cancelled = true;
      ac.abort();
    };
  }, [loadAllBoards, loadFolders, loadNotifications, navigate]);

  useEffect(() => {
    if (!sessionReady) return;
    if (viewParam === "recordings" && accountKind !== "school") {
      const next = new URLSearchParams(searchParams);
      next.delete("view");
      next.set("folder", "root");
      setSearchParams(next, { replace: true });
    }
  }, [accountKind, searchParams, sessionReady, setSearchParams, viewParam]);

  useEffect(() => {
    if (!sessionReady || cabinetView !== "recordings" || accountKind !== "school") return;
    void loadRecordings().catch((ex) => {
      setErr(ex instanceof Error ? ex.message : "Не удалось загрузить записи");
    });
  }, [accountKind, cabinetView, loadRecordings, sessionReady]);

  useEffect(() => {
    setSearchDraft(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (!sessionReady) return;
    void loadAllBoards(searchQuery).catch((ex) => {
      setErr(ex instanceof Error ? ex.message : "Не удалось загрузить данные");
    });
  }, [loadAllBoards, searchQuery, sessionReady]);

  useEffect(() => {
    if (!sessionReady) return;
    const timer = window.setTimeout(() => {
      const trimmed = searchDraft.trim();
      if (trimmed === searchQuery) return;
      const next = new URLSearchParams(searchParams);
      if (trimmed) next.set("q", trimmed);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 260);
    return () => window.clearTimeout(timer);
  }, [searchDraft, searchParams, searchQuery, sessionReady, setSearchParams]);

  const tree = useMemo(() => {
    const folderIds = new Set(activeFolderList.map((f) => f.id));
    const byParent: Record<string, FolderRow[]> = {};
    activeFolderList.forEach((f) => {
      const parentInSet = f.parent_id && folderIds.has(f.parent_id) ? f.parent_id : "root";
      if (!byParent[parentInSet]) byParent[parentInSet] = [];
      byParent[parentInSet].push(f);
    });
    Object.values(byParent).forEach((arr) =>
      arr.sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name, "ru"))
    );

    const rows: FolderTreeRow[] = [];
    function walk(parentKey: string, depth: number, hidden: boolean) {
      (byParent[parentKey] || []).forEach((f) => {
        const kids = byParent[f.id] || [];
        const hasChildren = kids.length > 0;
        if (!hidden) rows.push({ folder: f, depth, hasChildren });
        const isCollapsed = collapsedIds.has(f.id);
        walk(f.id, depth + 1, hidden || isCollapsed);
      });
    }
    walk("root", 0, false);
    return rows;
  }, [activeFolderList, collapsedIds]);

  /**
   * «Мои» — доски, которые человек создал сам. «Со мной» — всё остальное, что
   * ему видно: по общей папке, по приглашению в доску и по ссылке.
   *
   * Раньше «Со мной» показывал только доски из общих папок, поэтому доска,
   * открытая по ссылке (и даже та, куда позвали участником), нигде не
   * показывалась — раздел оставался пустым.
   */
  const isOwnBoard = (b: BoardSummary) => b.is_owner === true || b.access_via === "owner";
  const scopeBoards = useMemo(() => {
    if (cabinetSection === "shared") return allBoards.filter((b) => !isOwnBoard(b));
    return allBoards.filter(isOwnBoard);
  }, [allBoards, cabinetSection]);

  /** Доски, закреплённые в левой панели — из обоих разделов сразу. */
  const pinnedBoards = useMemo(
    () => allBoards.filter((b) => b.is_pinned),
    [allBoards]
  );

  const ownedBoardsCount = useMemo(() => {
    return allBoards.filter((b) => b.is_owner === true || b.access_via === "owner").length;
  }, [allBoards]);

  /**
   * Папки, видимые в этом разделе. Доска, которой со мной поделились, лежит в
   * папке владельца: её мне не показывают, и доска не попадала ни в одну папку
   * и ни в корень — раздел «Со мной» оставался пустым. Поэтому к корню относим
   * и доски из папок, которых я не вижу.
   */
  const visibleFolderIds = useMemo(() => {
    const ids = new Set<string>();
    for (const f of activeFolderList) ids.add(f.id);
    return ids;
  }, [activeFolderList]);

  const atRoot = useCallback(
    (b: BoardSummary) => !b.folder_id || !visibleFolderIds.has(b.folder_id),
    [visibleFolderIds]
  );

  const countRoot = scopeBoards.filter(atRoot).length;

  const countByFolder = useMemo(() => {
    const m: Record<string, number> = {};
    scopeBoards.forEach((b) => {
      if (b.folder_id && visibleFolderIds.has(b.folder_id)) {
        m[b.folder_id] = (m[b.folder_id] || 0) + 1;
      }
    });
    return m;
  }, [scopeBoards, visibleFolderIds]);

  const displayedBoards = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = (b: BoardSummary) =>
      !q || b.title.toLowerCase().includes(q);

    if (cabinetView === "recent") {
      return scopeBoards.filter(matchesSearch);
    }
    if (cabinetView === "starred") {
      return scopeBoards.filter((b) => b.is_starred && matchesSearch(b));
    }
    if (selectedFolderId) {
      return scopeBoards.filter((b) => b.folder_id === selectedFolderId && matchesSearch(b));
    }
    return scopeBoards.filter((b) => atRoot(b) && matchesSearch(b));
  }, [atRoot, cabinetView, scopeBoards, searchQuery, selectedFolderId]);

  const recentGroups = useMemo(() => {
    if (cabinetView !== "recent") return [];
    return groupBoardsByRecency(displayedBoards);
  }, [cabinetView, displayedBoards]);

  const starredCount = useMemo(
    () => scopeBoards.filter((b) => b.is_starred).length,
    [scopeBoards]
  );

  function flashOk(msg: string) {
    setOk(msg);
    setErr("");
    window.setTimeout(() => setOk(""), 4000);
  }

  function boardLimitReached(): boolean {
    if (maxBoardsLimit >= 0 && ownedBoardsCount >= maxBoardsLimit) return true;
    return !canCreateBoardByPlan && maxBoardsLimit >= 0;
  }

  function boardLimitMessage(): string {
    const code = (planCode ?? "free").toUpperCase();
    if (maxBoardsLimit >= 0) {
      return `На тарифе ${code} можно иметь до ${maxBoardsLimit} досок. Сейчас: ${ownedBoardsCount}. Удалите лишние или повысьте тариф.`;
    }
    return "Лимит досок для текущего тарифа исчерпан.";
  }

  async function onCreateBoard(e: FormEvent) {
    e.preventDefault();
    if (boardLimitReached()) {
      setErr(boardLimitMessage());
      return;
    }
    try {
      const title = newBoardTitle.trim() || "Новая доска";
      const body: { title: string; folder_id?: string } = { title };
      if (selectedFolderId) body.folder_id = selectedFolderId;
      await apiFetch("/api/v1/boards", { method: "POST", json: body });
      setNewBoardTitle("");
      flashOk("Доска создана");
      await loadAllBoards(searchQuery);
      setCanCreateBoardByPlan(maxBoardsLimit < 0 || ownedBoardsCount + 1 < maxBoardsLimit);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function onCreateFolder(e: FormEvent) {
    e.preventDefault();
    const name = newFolderName.trim();
    if (!name) return;
    try {
      const body: { name: string; parent_id?: string } = { name };
      if (selectedFolderId) body.parent_id = selectedFolderId;
      await apiFetch("/api/v1/folders", { method: "POST", json: body });
      setNewFolderName("");
      flashOk("Папка создана");
      await loadFolders();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function onRenameBoardSubmit(e: FormEvent) {
    e.preventDefault();
    if (!renameBoard) return;
    try {
      await apiFetch(`/api/v1/boards/${renameBoard.id}`, {
        method: "PATCH",
        json: {
          title: renameBoard.title.trim() || "Доска",
          description: renameBoard.description.trim(),
        },
      });
      setRenameBoard(null);
      flashOk("Сохранено");
      await loadAllBoards(searchQuery);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function onMoveBoardSubmit(e: FormEvent) {
    e.preventDefault();
    if (!moveBoardId) return;
    try {
      await apiFetch(`/api/v1/boards/${moveBoardId}`, {
        method: "PATCH",
        json: { folder_id: moveFolderId === "" ? null : moveFolderId },
      });
      setMoveBoardId(null);
      flashOk("Доска перенесена");
      await loadAllBoards(searchQuery);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function onDeleteBoardConfirm() {
    if (!deleteBoardId) return;
    try {
      await apiFetch(`/api/v1/boards/${deleteBoardId}`, { method: "DELETE" });
      setDeleteBoardId(null);
      flashOk("Доска в корзине");
      await loadAllBoards(searchQuery);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function onRenameFolderSubmit(e: FormEvent) {
    e.preventDefault();
    if (!selectedFolderId) return;
    try {
      await apiFetch(`/api/v1/folders/${selectedFolderId}`, {
        method: "PATCH",
        json: { name: renameFolderName.trim() || panelTitle },
      });
      setRenameFolderOpen(false);
      flashOk("Папка переименована");
      await loadFolders();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  async function onDeleteFolderConfirm(keepBoards: boolean) {
    if (!selectedFolderId) return;
    try {
      const qs = keepBoards ? "?mode=keep_boards" : "";
      await apiFetch(`/api/v1/folders/${selectedFolderId}${qs}`, { method: "DELETE" });
      setDeleteFolderOpen(false);
      setFolder(null);
      flashOk(
        keepBoards
          ? "Папка удалена. Доски перенесены в «Без папки»."
          : "Папка и доски в ней удалены"
      );
      await loadFolders();
      await loadAllBoards(searchQuery);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
      setDeleteFolderOpen(false);
    }
  }

  async function onMoveFolderSubmit(e: FormEvent) {
    e.preventDefault();
    if (!moveFolderTarget) return;
    try {
      await apiFetch(`/api/v1/folders/${moveFolderTarget.id}/move`, {
        method: "POST",
        json: { parent_id: moveFolderParentId === "" ? null : moveFolderParentId },
      });
      setMoveFolderTarget(null);
      flashOk("Папка перенесена");
      await loadFolders();
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  function toggleCollapse(folderId: string) {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) next.delete(folderId);
      else next.add(folderId);
      return next;
    });
  }

  const moveBoardFolderOptions = useMemo(
    () => buildFolderTreeOptions(folders),
    [folders]
  );

  const moveFolderParentOptions = useMemo(() => {
    if (!moveFolderTarget) return buildFolderTreeOptions(folders);
    const exclude = new Set<string>([moveFolderTarget.id]);
    function collectDescendants(parentId: string) {
      folders.forEach((f) => {
        if (f.parent_id === parentId) {
          exclude.add(f.id);
          collectDescendants(f.id);
        }
      });
    }
    collectDescendants(moveFolderTarget.id);
    return buildFolderTreeOptions(folders, exclude);
  }, [folders, moveFolderTarget]);

  function onPickFolderTemplate(kind: "class" | "student") {
    setNewFolderName(kind === "class" ? "Класс 8А" : "Ученик Иванов");
    window.setTimeout(() => {
      newFolderInputRef.current?.focus();
      newFolderInputRef.current?.select();
    }, 0);
  }

  async function onToggleStar(boardId: string, next: boolean) {
    try {
      setStarBusyId(boardId);
      await apiFetch(`/api/v1/boards/${boardId}/star`, {
        method: next ? "PUT" : "DELETE",
      });
      setAllBoards((prev) =>
        prev.map((b) => (b.id === boardId ? { ...b, is_starred: next } : b))
      );
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    } finally {
      setStarBusyId(null);
    }
  }

  /** Закрепить доску в левой панели кабинета (или открепить). */
  async function onTogglePin(boardId: string, next: boolean) {
    try {
      await setBoardPinned(boardId, next);
      setAllBoards((prev) =>
        prev.map((b) => (b.id === boardId ? { ...b, is_pinned: next } : b))
      );
      flashOk(next ? "Доска закреплена в панели" : "Доска убрана из панели");
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  /** Убрать у себя доску, добавленную по ссылке. Саму доску это не трогает. */
  async function onRemoveShortcut(board: BoardSummary) {
    try {
      await removeBoardShortcut(board.id);
      setAllBoards((prev) => prev.filter((b) => b.id !== board.id));
      flashOk("Доска убрана из вашего списка");
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    }
  }

  /** Добавить доску себе по ссылке-приглашению. */
  async function onAddByLink() {
    const value = addLinkValue.trim();
    if (!value || addLinkBusy) return;
    try {
      setAddLinkBusy(true);
      const res = await addBoardByLink(value);
      if (!res.ok) {
        setErr(res.error);
        return;
      }
      setAddLinkValue("");
      setAddLinkOpen(false);
      const boards = await loadAllBoards("");
      const added = boards.find((b) => b.id === res.boardId);
      if (!added) {
        // Честно: сервер доску принял, но в списке её нет. Открыть её человек
        // всё равно может — ссылка рабочая.
        setErr(
          "Доска сохранена, но в списке не появилась. Откройте её по ссылке: /board/" +
            res.boardId
        );
        return;
      }
      // Показываем ровно то место, где доска теперь лежит: нужный раздел,
      // корень папок и без поискового запроса — иначе она опять «не видна».
      const own = added.is_owner === true || added.access_via === "owner";
      const next = new URLSearchParams(searchParams);
      next.delete("q");
      next.delete("view");
      next.set("folder", "root");
      if (own) next.delete("section");
      else next.set("section", "shared");
      setSearchParams(next, { replace: true });
      flashOk(
        own
          ? "Эта доска и так ваша — она в разделе «Мои»"
          : "Доска добавлена — она в разделе «Со мной»"
      );
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    } finally {
      setAddLinkBusy(false);
    }
  }

  async function onDuplicateBoard(boardId: string) {
    if (boardLimitReached()) {
      setErr(boardLimitMessage());
      return;
    }
    try {
      setDuplicateBusyId(boardId);
      await apiFetch(`/api/v1/boards/${boardId}/duplicate`, { method: "POST" });
      flashOk("Копия доски создана");
      await loadAllBoards(searchQuery);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    } finally {
      setDuplicateBusyId(null);
    }
  }

  async function onMockPlanChange(nextPlan: "free" | "pro" | "business") {
    try {
      setPlanBusy(true);
      await apiFetch<{ plan_code: string }>("/api/v1/billing/mock-checkout", {
        method: "POST",
        json: { plan_code: nextPlan },
      });
      setPlanCode(nextPlan);
      const me = await apiFetch<MeResponse>("/api/v1/me");
      if (me.plan_limits) {
        setMaxBoardsLimit(me.plan_limits.max_boards);
        setCanCreateBoardByPlan(Boolean(me.plan_limits.can_create_board));
      } else {
        setMaxBoardsLimit(nextPlan === "pro" ? 30 : nextPlan === "business" ? -1 : 3);
        setCanCreateBoardByPlan(true);
      }
      flashOk(`Тариф переключен на ${nextPlan.toUpperCase()} (тестовый режим)`);
      await loadAllBoards(searchQuery);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : "Ошибка");
    } finally {
      setPlanBusy(false);
    }
  }

  if (!sessionReady) {
    return (
      <>
        <BezpartHeader mode="logged-in" userName="…" />
        <div className="wb-cabinet-wrap wb-cabinet-wrap--loading">
          <p className="wb-cabinet-loading">Загрузка кабинета…</p>
        </div>
      </>
    );
  }

  return (
    <>
      <BezpartHeader
        mode="logged-in"
        userName={userName || "Пользователь"}
        logoutHref={accountKind === "school" ? "/school" : "/"}
        profileMenu={{
          planCode,
          accountKind,
          showTrash: cabinetSection === "mine",
          adminSchoolAllowed,
          adminExternalAllowed,
          notificationCount: notifications.length,
        }}
      />

      <CabinetLimitBanner
        accountKind={accountKind}
        planCode={planCode}
        ownedBoardsCount={ownedBoardsCount}
        maxBoardsLimit={maxBoardsLimit}
        upgradeBusy={planBusy}
        onUpgrade={() => void onMockPlanChange("pro")}
      />

      <div className="wb-cabinet-wrap">
        <div className="wb-cabinet-layout">
          <aside className="wb-cabinet-sidebar" aria-label="Навигация кабинета">
            <div className="wb-cabinet-sidebar__head">
              <div className="wb-cabinet-sidebar__search">
                <span className="wb-cabinet-sidebar__search-icon">
                  <IconSearch />
                </span>
                <input
                  id="cabinet-sidebar-search"
                  className="wb-cabinet-sidebar__search-input"
                  type="search"
                  placeholder="Поиск досок"
                  maxLength={100}
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                />
              </div>
              <div className="wb-cabinet-sidebar__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={cabinetSection === "mine"}
                  className={`wb-cabinet-sidebar__tab${cabinetSection === "mine" ? " is-active" : ""}`}
                  onClick={() => setSection("mine")}
                >
                  Мои
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={cabinetSection === "shared"}
                  className={`wb-cabinet-sidebar__tab${cabinetSection === "shared" ? " is-active" : ""}`}
                  onClick={() => setSection("shared")}
                >
                  Со мной
                </button>
              </div>
              <div className="wb-cabinet-sidebar__section-title">Обзор</div>
            </div>
            <div className="wb-cabinet-sidebar__body">
              <nav className="wb-cabinet-sidebar__nav">
                <button
                  type="button"
                  className={`wb-cabinet-sidebar__item${cabinetView === "recent" ? " is-active" : ""}`}
                  onClick={() => setView("recent")}
                >
                  <span className="wb-cabinet-sidebar__icon">
                    <IconClock />
                  </span>
                  <span className="wb-cabinet-sidebar__label">Недавние</span>
                </button>
                <button
                  type="button"
                  className={`wb-cabinet-sidebar__item${cabinetView === "starred" ? " is-active" : ""}`}
                  onClick={() => setView("starred")}
                >
                  <span className="wb-cabinet-sidebar__icon">
                    <IconStarNav />
                  </span>
                  <span className="wb-cabinet-sidebar__label">Избранное</span>
                  <span className="wb-cabinet-sidebar__count">{starredCount}</span>
                </button>
                {showRecordingsNav ? (
                  <button
                    type="button"
                    className={`wb-cabinet-sidebar__item${cabinetView === "recordings" ? " is-active" : ""}`}
                    onClick={() => setView("recordings")}
                  >
                    <span className="wb-cabinet-sidebar__icon">
                      <IconMic />
                    </span>
                    <span className="wb-cabinet-sidebar__label">Записи</span>
                    <span className="wb-cabinet-sidebar__count">{recordings.length || ""}</span>
                  </button>
                ) : null}
              </nav>
              {pinnedBoards.length ? (
                <>
                  <div className="wb-cabinet-sidebar__section-title">Закреплённые</div>
                  <nav className="wb-cabinet-sidebar__nav">
                    {pinnedBoards.map((b) => (
                      <Link
                        key={b.id}
                        to={`/board/${b.id}`}
                        className="wb-cabinet-sidebar__item"
                        title={b.title}
                      >
                        <span className="wb-cabinet-sidebar__icon">
                          <IconPinNav />
                        </span>
                        <span className="wb-cabinet-sidebar__label">{b.title}</span>
                      </Link>
                    ))}
                  </nav>
                </>
              ) : null}
              <div className="wb-cabinet-sidebar__section-title">Доска по ссылке</div>
              <div className="wb-cabinet-sidebar__add-link">
                {addLinkOpen ? (
                  <>
                    <input
                      className="wb-input wb-cabinet-sidebar__add-link-input"
                      placeholder="Вставьте ссылку"
                      value={addLinkValue}
                      autoFocus
                      onChange={(e) => setAddLinkValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void onAddByLink();
                        if (e.key === "Escape") setAddLinkOpen(false);
                      }}
                    />
                    <div className="wb-cabinet-sidebar__add-link-actions">
                      <button
                        type="button"
                        className="wb-btn wb-btn--primary wb-btn--sm"
                        disabled={addLinkBusy}
                        onClick={() => void onAddByLink()}
                      >
                        {addLinkBusy ? "Добавляем…" : "Добавить"}
                      </button>
                      <button
                        type="button"
                        className="wb-btn wb-btn--ghost wb-btn--sm"
                        onClick={() => setAddLinkOpen(false)}
                      >
                        Отмена
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="wb-cabinet-sidebar__item"
                    onClick={() => setAddLinkOpen(true)}
                  >
                    <span className="wb-cabinet-sidebar__icon">
                      <IconPlusNav />
                    </span>
                    <span className="wb-cabinet-sidebar__label">Добавить по ссылке</span>
                  </button>
                )}
              </div>
              <div className="wb-cabinet-sidebar__section-title wb-cabinet-sidebar__section-title--folders">
                Папки
              </div>
              {cabinetSection === "shared" && sharedFolders.length === 0 ? (
                <p className="wb-cabinet-sidebar__empty">Пока нет общих папок</p>
              ) : null}
              <nav className="wb-cabinet-sidebar__nav">
              <button
                type="button"
                className={`wb-cabinet-sidebar__item${cabinetView === "folder" && selectedFolderId === null ? " is-active" : ""}`}
                onClick={() => setFolder(null)}
              >
                <span className="wb-cabinet-sidebar__icon">
                  <IconList />
                </span>
                <span className="wb-cabinet-sidebar__label">Без папки</span>
                <span className="wb-cabinet-sidebar__count">{countRoot}</span>
              </button>
              {tree.map(({ folder, depth, hasChildren }) => (
                <div
                  key={folder.id}
                  className={`wb-cabinet-sidebar__item-wrap${selectedFolderId === folder.id ? " is-active-wrap" : ""}`}
                  style={{ ["--wb-folder-depth" as string]: depth }}
                >
                  {hasChildren ? (
                    <button
                      type="button"
                      className="wb-cabinet-sidebar__collapse"
                      aria-label={collapsedIds.has(folder.id) ? "Развернуть" : "Свернуть"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCollapse(folder.id);
                      }}
                    >
                      <IconChevron open={!collapsedIds.has(folder.id)} />
                    </button>
                  ) : (
                    <span className="wb-cabinet-sidebar__collapse-spacer" aria-hidden />
                  )}
                  <button
                    type="button"
                    className={`wb-cabinet-sidebar__item is-nested${cabinetView === "folder" && selectedFolderId === folder.id ? " is-active" : ""}`}
                    onClick={() => setFolder(folder.id)}
                  >
                    <span className="wb-cabinet-sidebar__icon">
                      <IconFolder />
                    </span>
                    <span className="wb-cabinet-sidebar__label">{folder.name}</span>
                    <span className="wb-cabinet-sidebar__count">
                      {countByFolder[folder.id] || 0}
                    </span>
                  </button>
                </div>
              ))}
              </nav>
              {cabinetSection === "mine" ? (
                <Link className="wb-cabinet-sidebar__footer-link" to="/trash">
                  Корзина
                </Link>
              ) : null}
            </div>
            {cabinetSection === "mine" ? (
              <div className="wb-cabinet-sidebar__create-folder">
                <form onSubmit={onCreateFolder}>
                  <label className="wb-cabinet-sidebar__mini-label" htmlFor="new-folder">
                    {selectedFolderId ? "Подпапка" : "Новая папка"}
                  </label>
                  <div className="wb-cabinet-sidebar__folder-row">
                    <input
                      id="new-folder"
                      ref={newFolderInputRef}
                      className="wb-cabinet-sidebar__input"
                      type="text"
                      placeholder={selectedFolderId ? "Название подпапки" : "Название"}
                      maxLength={255}
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                    />
                    <button className="wb-cabinet-sidebar__add-btn" type="submit" title="Создать папку">
                      +
                    </button>
                  </div>
                  <div className="wb-cabinet-sidebar__templates">
                    <button
                      type="button"
                      className="wb-cabinet-sidebar__template-btn"
                      onClick={() => onPickFolderTemplate("class")}
                    >
                      + Класс
                    </button>
                    <button
                      type="button"
                      className="wb-cabinet-sidebar__template-btn"
                      onClick={() => onPickFolderTemplate("student")}
                    >
                      + Ученик
                    </button>
                  </div>
                </form>
              </div>
            ) : null}
          </aside>

          <main className="wb-cabinet-main-panel">
            {ok ? (
              <p className="wb-cabinet-toast wb-cabinet-toast--ok" role="status">
                {ok}
              </p>
            ) : null}
            {err ? (
              <p className="wb-cabinet-toast wb-cabinet-toast--err" role="alert">
                {err}
              </p>
            ) : null}

            <header className="wb-cabinet-panel-head">
              <div className="wb-cabinet-panel-head__titles">
                <h1 className="wb-cabinet-panel-head__title">{panelTitle}</h1>
                <p className="wb-cabinet-panel-head__sub">{panelSub}</p>
              </div>
              {selectedFolderId && cabinetSection === "mine" && cabinetView === "folder" ? (
                <div className="wb-cabinet-panel-head__folder-tools">
                  <Link
                    className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                    to={`/folder/${selectedFolderId}/settings`}
                  >
                    Доступ
                  </Link>
                  <button
                    type="button"
                    className="wb-board-card__action-btn"
                    onClick={() => {
                      setRenameFolderName(panelTitle);
                      setRenameFolderOpen(true);
                    }}
                  >
                    Переименовать
                  </button>
                  <button
                    type="button"
                    className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                    onClick={() => {
                      const f = folders.find((x) => x.id === selectedFolderId);
                      setMoveFolderTarget({
                        id: selectedFolderId,
                        parent_id: f?.parent_id ?? null,
                      });
                      setMoveFolderParentId(f?.parent_id || "");
                    }}
                  >
                    Перенести
                  </button>
                  <button
                    type="button"
                    className="wb-board-card__action-btn wb-board-card__action-btn--danger"
                    onClick={() => setDeleteFolderOpen(true)}
                  >
                    Удалить
                  </button>
                </div>
              ) : null}
              {selectedFolderId && cabinetSection === "shared" && cabinetView === "folder" ? (
                <div className="wb-cabinet-panel-head__folder-tools">
                  <Link
                    className="wb-board-card__action-btn"
                    to={`/folder/${selectedFolderId}/settings`}
                  >
                    Доступ
                  </Link>
                </div>
              ) : null}
            </header>

            {cabinetSection === "mine" && notifications.length > 0 ? (
              <section className="wb-cabinet-notifications" aria-label="Уведомления">
                <h2 className="wb-cabinet-notifications__title">Комментарии на досках</h2>
                <ul className="wb-cabinet-notifications__list">
                  {notifications.slice(0, 5).map((n) => (
                    <li key={n.id}>
                      <Link to={n.open_path} className="wb-cabinet-notifications__item">
                        <strong>{n.board_title}</strong>
                        {n.preview ? `: ${n.preview}` : ""}
                        {n.actor_email ? (
                          <span className="wb-cabinet-notifications__meta"> · {n.actor_email}</span>
                        ) : null}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {cabinetSection === "mine" && cabinetView === "folder" ? (
              <section className="wb-cabinet-panel-create" aria-label="Новая доска">
                {accountKind === "school" ? (
                  <p className="wb-cabinet-plan-hint wb-cabinet-plan-hint--school">
                    Тариф: School — без ограничений для школьного аккаунта.
                  </p>
                ) : (
                  <div className="wb-cabinet-plan-hint">
                    <p className="wb-cabinet-plan-hint__text">
                      Тариф: {planCode?.toUpperCase() || "FREE"}.
                      {` `}
                      Доски: {ownedBoardsCount}
                      {maxBoardsLimit >= 0 ? ` из ${maxBoardsLimit}` : " (без лимита)"}.
                      {` `}
                      На Free действуют лимиты (доски/папки/шаринг/медиа).
                    </p>
                    {boardLimitReached() ? (
                      <p className="wb-cabinet-plan-hint__text" style={{ marginTop: 6 }}>
                        {boardLimitMessage()}
                      </p>
                    ) : null}
                    <div className="wb-cabinet-plan-hint__actions">
                      <button
                        type="button"
                        className="wb-board-card__action-btn"
                        disabled={planBusy || (planCode ?? "free") === "pro"}
                        onClick={() => void onMockPlanChange("pro")}
                      >
                        {planBusy ? "Обновляем…" : "Купить Pro (тест)"}
                      </button>
                      <button
                        type="button"
                        className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                        disabled={planBusy || (planCode ?? "free") === "business"}
                        onClick={() => void onMockPlanChange("business")}
                      >
                        Business (тест)
                      </button>
                      <button
                        type="button"
                        className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                        disabled={planBusy || (planCode ?? "free") === "free"}
                        onClick={() => void onMockPlanChange("free")}
                      >
                        Вернуть Free
                      </button>
                    </div>
                  </div>
                )}
                {adminBillingAllowed ? (
                  <div className="wb-cabinet-plan-hint__actions">
                    {adminSchoolAllowed ? (
                      <Link className="wb-board-card__action-btn" to="/admin/school">
                        Админка школы
                      </Link>
                    ) : null}
                    {adminExternalAllowed ? (
                      <Link className="wb-board-card__action-btn" to="/admin/external">
                        Админка внешних
                      </Link>
                    ) : null}
                  </div>
                ) : null}
                <form className="wb-cabinet-panel-create__form" onSubmit={onCreateBoard}>
                  <span className="wb-cabinet-panel-create__inline-label">Новая доска</span>
                  <input
                    className="wb-cabinet-panel-create__input"
                    type="text"
                    placeholder="Название доски"
                    maxLength={255}
                    value={newBoardTitle}
                    onChange={(e) => setNewBoardTitle(e.target.value)}
                  />
                  <button type="submit" className="wb-cabinet-panel-create__submit">
                    Создать
                  </button>
                </form>
              </section>
            ) : null}

            <section className="wb-cabinet-boards" aria-label="Список досок">
              {cabinetView === "recordings" ? (
                recordingsLoading ? (
                  <p className="wb-cabinet-loading">Загрузка записей…</p>
                ) : recordings.length === 0 ? (
                  <CabinetEmptyState
                    title="Записей пока нет"
                    text="Здесь появляются только автозаписи школьных уроков. Обычные звонки на сервер не сохраняются."
                  />
                ) : (
                  <div className="wb-board-stack">
                    {recordings.map((rec) => {
                      const when = formatBoardUpdated(rec.recorded_at);
                      const size = formatBytes(rec.size_bytes);
                      return (
                        <article
                          key={rec.room_id}
                          className="wb-board-card wb-board-card--list wb-lk-card"
                        >
                          <div className="wb-board-card__row">
                            <div className="wb-board-card__open-zone wb-board-card__open-zone--static">
                              <span className="wb-board-card__title">{rec.board_title}</span>
                              <span className="wb-board-card__badge">Урок</span>
                              {rec.label ? (
                                <span className="wb-board-card__badge">{rec.label}</span>
                              ) : null}
                              {when ? (
                                <span className="wb-board-card__meta">· {when}</span>
                              ) : null}
                              {size ? (
                                <span className="wb-board-card__meta">· {size}</span>
                              ) : null}
                            </div>
                            <div className="wb-board-card__actions">
                              <Link
                                className="wb-board-card__action-btn wb-board-card__action-btn--ghost"
                                to={`/board/${rec.board_id}`}
                              >
                                Доска
                              </Link>
                              <a
                                className="wb-board-card__action-btn"
                                href={rec.download_path}
                                download
                              >
                                Скачать
                              </a>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )
              ) : displayedBoards.length === 0 ? (
                <CabinetEmptyState
                  title={
                    cabinetView === "starred"
                      ? "Нет избранных досок"
                      : cabinetView === "recent"
                        ? "Недавних досок нет"
                        : "Пока нет досок"
                  }
                  text={
                    cabinetView === "starred"
                      ? "Нажмите звёздочку у доски, чтобы добавить её сюда."
                      : cabinetView === "recent"
                        ? "Откройте или создайте доску — она появится в этом списке."
                        : "Создайте первую доску — она появится в этом списке."
                  }
                />
              ) : cabinetView === "recent" ? (
                <div className="wb-cabinet-recent-groups">
                  {recentGroups.map((group) => (
                    <section key={group.label} className="wb-cabinet-recent-group">
                      <h2 className="wb-cabinet-recent-group__title">{group.label}</h2>
                      <div className="wb-board-stack">
                        {group.boards.map((b) => (
                          <CabinetBoardRow
                            key={b.id}
                            board={b}
                            cabinetSection={cabinetSection}
                            duplicateBusy={duplicateBusyId === b.id}
                            starBusy={starBusyId === b.id}
                            onToggleStar={onToggleStar}
                            onRename={(board) =>
                        setRenameBoard({
                          id: board.id,
                          title: board.title,
                          description: board.description ?? "",
                        })
                      }
                            onMove={(board) => {
                              setMoveBoardId(board.id);
                              setMoveFolderId(board.folder_id || "");
                            }}
                            onDuplicate={onDuplicateBoard}
                            onDelete={setDeleteBoardId}
                            onShare={setShareBoard}
                            onTogglePin={onTogglePin}
                            onRemoveShortcut={onRemoveShortcut}
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="wb-board-stack">
                  {displayedBoards.map((b) => (
                    <CabinetBoardRow
                      key={b.id}
                      board={b}
                      cabinetSection={cabinetSection}
                      duplicateBusy={duplicateBusyId === b.id}
                      starBusy={starBusyId === b.id}
                      onToggleStar={onToggleStar}
                      onRename={(board) =>
                        setRenameBoard({
                          id: board.id,
                          title: board.title,
                          description: board.description ?? "",
                        })
                      }
                      onMove={(board) => {
                        setMoveBoardId(board.id);
                        setMoveFolderId(board.folder_id || "");
                      }}
                      onDuplicate={onDuplicateBoard}
                      onDelete={setDeleteBoardId}
                      onShare={setShareBoard}
                      onTogglePin={onTogglePin}
                      onRemoveShortcut={onRemoveShortcut}
                    />
                  ))}
                </div>
              )}
            </section>
          </main>
        </div>
      </div>

      <CabinetShareDialog
        boardId={shareBoard?.id ?? ""}
        boardTitle={shareBoard?.title ?? ""}
        open={shareBoard !== null}
        onClose={() => setShareBoard(null)}
      />

      <dialog className="wb-modal" open={renameBoard !== null}>
        <div className="wb-modal__box">
          <h2 className="wb-modal__heading">Название и описание</h2>
          <form onSubmit={onRenameBoardSubmit}>
            <label className="wb-label" htmlFor="rename-board-input">
              Название
            </label>
            <input
              id="rename-board-input"
              className="wb-input"
              value={renameBoard?.title ?? ""}
              onChange={(e) =>
                setRenameBoard((prev) =>
                  prev ? { ...prev, title: e.target.value } : prev
                )
              }
              maxLength={255}
              required
              autoFocus
            />
            <label className="wb-label" htmlFor="board-description-input">
              Описание
            </label>
            <textarea
              id="board-description-input"
              className="wb-input wb-textarea"
              value={renameBoard?.description ?? ""}
              onChange={(e) =>
                setRenameBoard((prev) =>
                  prev ? { ...prev, description: e.target.value } : prev
                )
              }
              maxLength={2000}
              rows={4}
              placeholder="Для кого эта доска и что на ней"
            />
            <div className="wb-modal__footer">
              <button
                type="button"
                className="wb-modal__btn wb-modal__btn--ghost"
                onClick={() => setRenameBoard(null)}
              >
                Отмена
              </button>
              <button type="submit" className="wb-modal__btn wb-modal__btn--primary">
                Сохранить
              </button>
            </div>
          </form>
        </div>
      </dialog>

      <dialog className="wb-modal" open={moveBoardId !== null}>
        <div className="wb-modal__box">
          <h2 className="wb-modal__heading">Перенести доску</h2>
          <form onSubmit={onMoveBoardSubmit}>
            <label className="wb-label" htmlFor="move-folder">
              Папка
            </label>
            <select
              id="move-folder"
              className="wb-select"
              value={moveFolderId}
              onChange={(e) => setMoveFolderId(e.target.value)}
            >
              <option value="">Без папки</option>
              {moveBoardFolderOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
            <div className="wb-modal__footer">
              <button
                type="button"
                className="wb-modal__btn wb-modal__btn--ghost"
                onClick={() => setMoveBoardId(null)}
              >
                Отмена
              </button>
              <button type="submit" className="wb-modal__btn wb-modal__btn--primary">
                Перенести
              </button>
            </div>
          </form>
        </div>
      </dialog>

      <dialog className="wb-modal" open={deleteBoardId !== null}>
        <div className="wb-modal__box">
          <h2 className="wb-modal__heading">Удалить доску?</h2>
          <p className="wb-modal__hint">Доска будет перемещена в корзину.</p>
          <div className="wb-modal__footer">
            <button
              type="button"
              className="wb-modal__btn wb-modal__btn--ghost"
              onClick={() => setDeleteBoardId(null)}
            >
              Отмена
            </button>
            <button
              type="button"
              className="wb-modal__btn wb-modal__btn--danger"
              onClick={() => void onDeleteBoardConfirm()}
            >
              В корзину
            </button>
          </div>
        </div>
      </dialog>

      <dialog className="wb-modal" open={renameFolderOpen}>
        <div className="wb-modal__box">
          <h2 className="wb-modal__heading">Переименовать папку</h2>
          <form onSubmit={onRenameFolderSubmit}>
            <label className="wb-label" htmlFor="rename-folder-input">
              Название
            </label>
            <input
              id="rename-folder-input"
              className="wb-input"
              value={renameFolderName}
              onChange={(e) => setRenameFolderName(e.target.value)}
              maxLength={255}
              required
            />
            <div className="wb-modal__footer">
              <button
                type="button"
                className="wb-modal__btn wb-modal__btn--ghost"
                onClick={() => setRenameFolderOpen(false)}
              >
                Отмена
              </button>
              <button type="submit" className="wb-modal__btn wb-modal__btn--primary">
                Сохранить
              </button>
            </div>
          </form>
        </div>
      </dialog>

      <dialog className="wb-modal" open={moveFolderTarget !== null}>
        <div className="wb-modal__box">
          <h2 className="wb-modal__heading">Перенести папку</h2>
          <form onSubmit={onMoveFolderSubmit}>
            <label className="wb-label" htmlFor="move-folder-parent">
              Родительская папка
            </label>
            <select
              id="move-folder-parent"
              className="wb-select"
              value={moveFolderParentId}
              onChange={(e) => setMoveFolderParentId(e.target.value)}
            >
              <option value="">Корень (без родителя)</option>
              {moveFolderParentOptions.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.label}
                </option>
              ))}
            </select>
            <div className="wb-modal__footer">
              <button
                type="button"
                className="wb-modal__btn wb-modal__btn--ghost"
                onClick={() => setMoveFolderTarget(null)}
              >
                Отмена
              </button>
              <button type="submit" className="wb-modal__btn wb-modal__btn--primary">
                Перенести
              </button>
            </div>
          </form>
        </div>
      </dialog>

      <dialog className="wb-modal" open={deleteFolderOpen}>
        <div className="wb-modal__box">
          <h2 className="wb-modal__heading">Удалить папку?</h2>
          <p className="wb-modal__hint">Выберите, что сделать с досками внутри папки.</p>
          <div className="wb-modal__folder-del-actions">
            <button
              type="button"
              className="wb-modal__btn wb-modal__btn--primary wb-modal__btn--block"
              onClick={() => void onDeleteFolderConfirm(true)}
            >
              Перенести доски в «Без папки»
            </button>
            <button
              type="button"
              className="wb-modal__btn wb-modal__btn--danger wb-modal__btn--block"
              onClick={() => void onDeleteFolderConfirm(false)}
            >
              Удалить папку и все доски
            </button>
          </div>
          <div className="wb-modal__footer wb-modal__footer--solo">
            <button
              type="button"
              className="wb-modal__btn wb-modal__btn--ghost"
              onClick={() => setDeleteFolderOpen(false)}
            >
              Отмена
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
}
